(() => {
  if (window.neutralsCollectionReady) return;
  window.neutralsCollectionReady = true;
  const instances = new Map();
  const motion = matchMedia('(prefers-reduced-motion: reduce)');
  const easing = 'cubic-bezier(.22,1,.36,1)';

  function initialize(root) {
    if (instances.has(root)) return;
    const events = new AbortController();
    const { signal } = events;
    const on = (node, name, listener) => node?.addEventListener(name, listener, { signal });
    let request = null;
    let busy = false;
    let observer = null;
    const entrances = new Set();
    const seenPages = new Set([location.href]);
    const grid = root.querySelector('[data-product-grid]');
    const status = root.querySelector('[data-load-status]');

    const description = root.querySelector('[data-description]');
    if (description && description.querySelector('.nc__description-full').textContent.trim().length > 240) {
      description.dataset.collapsible = 'true';
      on(description.querySelector('[data-description-toggle]'), 'click', event => {
        const expanded = !description.hasAttribute('data-expanded');
        description.toggleAttribute('data-expanded', expanded);
        event.currentTarget.setAttribute('aria-expanded', String(expanded));
        event.currentTarget.textContent = expanded ? (root.dataset.readLess || 'Read less') : (root.dataset.readMore || 'Read more');
      });
    }
    const sort = root.querySelector('[data-sort]');
    on(sort, 'change', () => sort.form.requestSubmit());
    let destroySort = () => {};
    if (sort && !sort.disabled) {
      const wrapper = document.createElement('div');
      wrapper.className = 'nc__sort-custom';
      const toggle = document.createElement('button');
      toggle.type = 'button';
      toggle.className = 'nc__sort-toggle';
      toggle.textContent = sort.selectedOptions[0]?.textContent || 'Sort';
      toggle.setAttribute('aria-label', `${root.dataset.sortLabel || 'Sort'}: ${toggle.textContent}`);
      toggle.setAttribute('aria-expanded', 'false');
      const choices = document.createElement('div');
      choices.className = 'nc__sort-options';
      choices.id = `${sort.id}-choices`;
      choices.hidden = true;
      toggle.setAttribute('aria-controls', choices.id);
      let animation;
      let version = 0;
      let opened = false;
      function setOpen(open, focus = false, immediate = false) {
        const current = ++version;
        animation?.cancel();
        opened = open;
        toggle.setAttribute('aria-expanded', String(open));
        choices.inert = !open;
        choices.hidden = false;
        if (focus) toggle.focus({ preventScroll: true });
        const finish = () => { if (version === current) { choices.hidden = !open; animation?.cancel(); } };
        if (immediate || motion.matches || !choices.animate) { finish(); return; }
        const frames = [{opacity:0,transform:'translateY(-5px)'},{opacity:1,transform:'translateY(0)'}];
        animation = choices.animate(open ? frames : frames.reverse(), {duration:160,easing,fill:'both'});
        animation.finished.then(finish).catch(() => {});
      }
      for (const option of sort.options) {
        const button = document.createElement('button');
        button.type = 'button';
        button.textContent = option.textContent;
        button.setAttribute('aria-label', option.textContent);
        button.disabled = option.disabled;
        button.setAttribute('aria-pressed', String(option.selected));
        on(button, 'click', () => {
          sort.value = option.value;
          setOpen(false, false, true);
          sort.form.requestSubmit();
        });
        choices.append(button);
      }
      wrapper.append(toggle, choices);
      sort.after(wrapper);
      sort.hidden = true;
      const label = sort.form.querySelector(`label[for="${sort.id}"]`);
      on(label, 'click', event => { event.preventDefault(); toggle.focus(); });
      on(toggle, 'click', () => setOpen(!opened));
      on(document, 'click', event => { if (opened && !wrapper.contains(event.target)) setOpen(false); });
      on(wrapper, 'keydown', event => {
        if (event.key === 'Escape' && opened) { event.preventDefault(); setOpen(false, true); }
      });
      on(wrapper, 'focusout', event => { if (opened && !wrapper.contains(event.relatedTarget)) setOpen(false); });
      on(motion, 'change', () => setOpen(opened, false, true));
      destroySort = () => { version++; animation?.cancel(); wrapper.remove(); sort.hidden = false; };
    }

    function observeCards(cards) {
      if (!observer || motion.matches) return;
      cards.forEach(card => observer.observe(card));
    }
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          observer.unobserve(entry.target);
          if (motion.matches || !entry.target.animate) return;
          const a = entry.target.animate([{ opacity: 0, transform: 'translateY(8px)' }, { opacity: 1, transform: 'none' }], { duration: 200, easing });
          entrances.add(a);
          a.finished.catch(() => {}).finally(() => entrances.delete(a));
        });
      }, { threshold: .04 });
      observeCards([...root.querySelectorAll('.nc-card')]);
    }
    function nextURL(href, base = location.href) {
      const next = new URL(href, base);
      if (next.origin !== location.origin || next.pathname !== location.pathname) throw new Error('Unexpected pagination destination');
      const current = new URL(location.href);
      for (const key of new Set(current.searchParams.keys())) {
        if (key === 'page' || key === 'section_id' || next.searchParams.has(key)) continue;
        current.searchParams.getAll(key).forEach(value => next.searchParams.append(key, value));
      }
      return next;
    }
    on(root, 'click', async event => {
      const link = event.target.closest('[data-load-more]');
      if (!link || !root.contains(link) || link.dataset.fallback || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button !== 0) return;
      event.preventDefault();
      if (busy || !grid) return;
      busy = true;
      link.setAttribute('aria-disabled', 'true');
      link.textContent = root.dataset.loadingLabel || 'LOADING…';
      grid.setAttribute('aria-busy', 'true');
      request = new AbortController();
      const timeout = setTimeout(() => request?.abort(), 15000);
      try {
        const url = nextURL(link.href);
        if (seenPages.has(url.href)) throw new Error('Repeated page');
        const response = await fetch(url, { signal: request.signal, credentials: 'same-origin' });
        if (!response.ok) throw new Error('Could not load page');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const section = [...html.querySelectorAll('[data-collection-page]')].find(el => el.dataset.sectionId === root.dataset.sectionId);
        const incoming = section?.querySelector('[data-product-grid]');
        if (!incoming) throw new Error('Missing collection grid');
        const ids = new Set([...grid.querySelectorAll('[data-product-id]')].map(card => card.dataset.productId));
        const added = [];
        for (const card of incoming.querySelectorAll('.nc-card[data-product-id]')) {
          if (ids.has(card.dataset.productId)) continue;
          ids.add(card.dataset.productId);
          added.push(document.importNode(card, true));
        }
        const nextLink = section.querySelector('[data-load-more]');
        const next = nextLink ? nextURL(nextLink.getAttribute('href'), url.href) : null;
        if (next && (next.href === url.href || seenPages.has(next.href))) throw new Error('Repeated next page');
        if (signal.aborted) return;
        grid.append(...added);
        seenPages.add(url.href);
        observeCards(added);
        status.textContent = `${added.length} additional ${added.length === 1 ? 'piece' : 'pieces'} loaded.${next ? '' : ' You have reached the end of the collection.'}`;
        if (next) link.href = next.href;
        else {
          if (document.activeElement === link) {
            status.tabIndex = -1;
            status.focus({ preventScroll: true });
          }
          link.remove();
        }
      } catch {
        if (!signal.aborted) {
          status.textContent = 'Unable to load more here. Select Load more to open the next page.';
          link.dataset.fallback = 'true';
        }
      } finally {
        clearTimeout(timeout);
        busy = false;
        grid.removeAttribute('aria-busy');
        link.removeAttribute('aria-disabled');
        link.textContent = root.dataset.loadLabel || 'LOAD MORE';
        request = null;
      }
    });
    on(motion, 'change', () => {
      entrances.forEach(a => a.cancel());

    });
    root.dataset.enhanced = 'true';
    instances.set(root, () => {
      events.abort(); request?.abort(); observer?.disconnect();
      destroySort();
      entrances.forEach(a => a.cancel());
      root.removeAttribute('data-enhanced');
      instances.delete(root);
    });
  }
  const scan = () => document.querySelectorAll('[data-collection-page]').forEach(initialize);
  document.addEventListener('shopify:section:load', scan);
  document.addEventListener('shopify:section:unload', event => {
    instances.forEach((destroy, root) => { if (event.target.contains(root)) destroy(); });
  });
  window.addEventListener('pagehide', () => instances.forEach(destroy => destroy()));
  window.addEventListener('pageshow', scan);
  scan();
})();
